// External dependencies
import { ErrorMessage } from '@hookform/error-message';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form@latest';
import type { SingleValue } from 'react-select';

// Utilities
import { _isNotEmpty, _notNil } from '@/littledash';

// Model types
import type { StudyCode } from '@/model/StudyCode.model';

// Support modules
import { useApiHook } from '@/support/Hooks/api/useApiHook';

// Internal components
import Select from '@/components/UI/Select/Select';

export interface StudyCodeOptionType {
  label: string;
  value: StudyCode;
}

interface StudyCodeLookupProps {
  updateStudyCode: (code: StudyCode) => void;
  settings?: boolean;
}

export const StudyCodeLookup: FC<StudyCodeLookupProps> = ({ updateStudyCode, settings }) => {
  const [studyCodeOptions, setStudyCodeOptions] = useState<Array<StudyCodeOptionType>>([]);
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();
  const codeWatch = useWatch({
    control,
    name: 'study_code',
  }) as StudyCode | undefined;

  const { response } = useApiHook({
    endpoint: 'GET /api/v1/study-codes',
  });

  useEffect(() => {
    if (response?.type === 'success') {
      if (response.body.length > 1) {
        setStudyCodeOptions(
          response.body.map((code) => ({
            label: code.title,
            value: code,
          }))
        );
      } else if (response.body.length === 1) {
        updateStudyCode(response.body[0]);
        setValue('study_code', response.body[0]);
      }
    }
  }, [response, updateStudyCode, setValue]);

  return settings !== true && _isNotEmpty(studyCodeOptions) ? (
    <Controller
      name="study_code"
      control={control}
      defaultValue={codeWatch}
      rules={{ required: 'Study code is required' }}
      render={({ field }) => (
        <div>
          <>
            <div>
              <label>Select a Study Code</label>
            </div>
            <Select
              onChange={(selectedOption: SingleValue<StudyCodeOptionType>) => {
                field.onChange(selectedOption?.value);
                if (_notNil(selectedOption)) {
                  updateStudyCode(selectedOption.value);
                }
              }}
              options={studyCodeOptions}
              value={_notNil(field.value) ? { value: field.value, label: field.value.title } : null}
              isMulti={false}
            />
            <ErrorMessage
              errors={errors}
              name="study_code"
              render={({ message }) => <p className="f6 red db pt2">{message}</p>}
            />
          </>
          {_notNil(codeWatch) && _notNil(codeWatch.composites) ? (
            <div className="bg-near-white w-50 pa2 mt2">
              {'Template: '}
              {codeWatch.composites
                .slice()
                .sort((comp_a, comp_b) => comp_a.order - comp_b.order)
                .reduce((acc, code) => acc + code.value, '')}
            </div>
          ) : null}
        </div>
      )}
    />
  ) : null;
};
